/** Platform Administration queries. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface ConfigField {
  name: string
  label: string
  type: string
  required: boolean
  secret: boolean
  description: string
  default: string | null
  choices: string[]
}

export interface ProviderDescriptor {
  category: string
  providerId: string
  displayName: string
  version: string
  description: string
  configSchema: ConfigField[]
  requiredSecrets: string[]
}

export interface ActiveBinding {
  binding: string
  providerId: string
  version: string
}

export interface HealthCheck {
  status: string
  detail: string | null
  latencyMs: number | null
  checkedAt: string
}

export interface AdminUser {
  id: string
  email: string
  displayName: string
  principalType: string
  organisation: string | null
  isActive: boolean
  lastLoginAt: string | null
  roles: string[]
}

export interface AdminRole {
  id: string
  code: string
  name: string
  description: string
  isSystem: boolean
  externalRoleValue: string
  permissions: string[]
}

export interface AuditEvent {
  id: string
  occurredAt: string
  actor: string
  actorType: string
  action: string
  resourceType: string | null
  resourceId: string | null
  outcome: string
  reason: string | null
  correlationId: string | null
}

export interface FeatureFlag {
  key: string
  description: string
  isEnabled: boolean
  scope?: string
}

export function useProviders() {
  return useQuery({
    queryKey: ['admin', 'providers'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/providers', {})
      return data as unknown as { available: ProviderDescriptor[]; active: ActiveBinding[] }
    },
  })
}

export function useSystemHealth() {
  return useQuery({
    queryKey: ['admin', 'health'],
    // Health is the one screen where a stale answer is actively misleading.
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/health', {})
      return data as unknown as { status: string; checks: Record<string, HealthCheck> }
    },
  })
}

export function useTestProvider() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (binding: string) => {
      const { data } = await api.POST('/api/v1/admin/providers/{binding}/test', {
        params: { path: { binding } },
      })
      return data as unknown as HealthCheck
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin', 'health'] }),
  })
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/users', {})
      return (data as unknown as { users: AdminUser[] }).users
    },
  })
}

export function useSetActivation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { data } = await api.POST('/api/v1/admin/users/{user_id}/activation', {
        params: { path: { user_id: id } },
        body: { is_active: isActive } as never,
      })
      return data
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export interface ExternalPerson {
  id: string
  displayName: string
  organisation: string | null
  email: string
  jobTitle: string | null
  isActive: boolean
}

export function useAddExternalPerson() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      display_name: string
      organisation: string
      email?: string
      job_title?: string
    }) => {
      const { data } = await api.POST('/api/v1/admin/external-people', { body: body as never })
      return data as unknown as ExternalPerson
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useUpdateExternalPerson() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) => {
      const { data } = await api.PATCH('/api/v1/admin/external-people/{person_id}', {
        params: { path: { person_id: id } },
        body: body as never,
      })
      return data as unknown as ExternalPerson
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/** `enabled: false` for anyone without `admin.users.view` — a call that is bound to be refused
 * also writes a denial to the audit trail, every time the screen loads. */
export function useRoles(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'roles'],
    enabled,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/roles', {})
      return (data as unknown as { roles: AdminRole[] }).roles
    },
  })
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['admin', 'flags'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/feature-flags', {})
      return (data as unknown as { flags: FeatureFlag[] }).flags
    },
  })
}

export function useAudit(filters: { action?: string; resource_type?: string; actor?: string }) {
  return useQuery({
    queryKey: ['admin', 'audit', filters],
    queryFn: async () => {
      const query = Object.fromEntries(
        Object.entries({ ...filters, limit: 100 }).filter(([, value]) => value !== ''),
      )
      const { data } = await api.GET('/api/v1/admin/audit', { params: { query: query as never } })
      return (data as unknown as { events: AuditEvent[] }).events
    },
  })
}

export interface ServiceMeta {
  application: string
  environment: string
  version: string
  api: {
    tenantId: string
    audience: string
    docsPath: string | null
    openapiPath: string | null
    supportsServicePrincipal: boolean
    supportsApiKeys: boolean
    apiKeyHeader?: string
    publicBaseUrl?: string
    assistantPrimerPath?: string
    feedPath?: string
    applicationAccess?: 'read-only'
    applicationRoles?: {
      value: string
      displayName: string
      description: string
      permissions: string[]
    }[]
  }
}

/** Service identity and how to call it, straight from the running deployment.
 *
 * The API Access page renders this rather than repeating it, so the instructions cannot
 * describe an environment other than the one you are signed in to.
 */
export function useMeta() {
  return useQuery({
    queryKey: ['admin', 'meta'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/meta', {})
      return data as unknown as ServiceMeta
    },
  })
}
